import { useTranslation } from 'react-i18next';
import { Check, Pencil, RotateCcw } from 'lucide-react';
import { SEVERITY_LABELS, SignalVisibilityType } from '@momentum/types';
import { useSeverityDotColor } from '../../shared/hooks/useSeverityDotColor';
import type { ReviewableSignal } from './types';

interface SignalRowProps {
  signal: ReviewableSignal;
  onToggleExclude: (index: number) => void;
  onEdit: (index: number) => void;
}

const severityName = (severity: number): string => {
  const entry = Object.entries(SEVERITY_LABELS).find(
    ([, value]) => value === severity
  );
  return entry ? entry[0] : 'Medium';
};

export const SignalRow = ({
  signal,
  onToggleExclude,
  onEdit,
}: SignalRowProps) => {
  const { t } = useTranslation('knowledge');
  const getSeverityDotColor = useSeverityDotColor();

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 transition-all ${
        signal.excluded ? 'bg-muted/50' : 'bg-[oklch(0.995_0.001_240)]'
      }`}
    >
      {/* Toggle include/exclude */}
      <button
        type="button"
        onClick={() => onToggleExclude(signal.index)}
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all cursor-pointer border-none ${
          signal.excluded
            ? 'border border-border bg-transparent'
            : 'bg-green-600/10 text-green-600 hover:bg-green-600/20'
        }`}
      >
        {!signal.excluded && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug line-clamp-2">
          {signal.title}
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span
              className={`h-2 w-2 rounded-full ${getSeverityDotColor(
                signal.severity
              )}`}
            />
            {severityName(signal.severity)}
          </span>
          <span>&middot;</span>
          <span>
            {signal.visibility === SignalVisibilityType.PUBLIC
              ? t('marketplacePublic')
              : t('marketplacePrivate')}
          </span>
          {signal.edited && (
            <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              {t('signalReview.edited')}
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 shrink-0">
        {signal.excluded ? (
          <button
            type="button"
            onClick={() => onToggleExclude(signal.index)}
            className="flex items-center gap-1 text-xs text-foreground bg-transparent border-none cursor-pointer px-2 py-1 rounded hover:bg-muted transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            {t('signalReview.restore')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onEdit(signal.index)}
            className="text-muted-foreground hover:text-foreground bg-transparent border-none cursor-pointer p-1.5 rounded hover:bg-muted transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
